import { ReactSVG } from 'react-svg'

import './styles.scss'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { ANIMATION_PLAY_STATE, AnimationActions } from '@AppState/animation'
import Analytics, { AnalyticEvents } from '@Util/analytics'
import { useCallback, useEffect } from 'react'
import { GameController } from '@Animation/game-control'
import { useTimeout } from '@Hooks/use-timeout'
import { ANIMATION_CONFIG_MAP } from '@Animation/animation-map'

const CLASS_NAME = 'gby-k2g-controls'
const GENERAL_BUTTON_CLASS = 'gby-k2g-control-button'

export type ButtonControl = {
  onClick: () => void
  disabled: boolean
}

export type AnimationControlProps = {
  prevButtonControl: ButtonControl
  jumpToDrawButtonControl?: ButtonControl
  nextButtonControl: ButtonControl
}

export const AnimationControls = (props: AnimationControlProps) => {
  const {
    isPaused,
    result,
    playState,
    message: animationMessage,
    animationIdentifier,
  } = useSelector((state: RootState) => state.animation)

  const hostConfig = useSelector((state: RootState) => state.hostConfig)

  const { autoPlay, gameSpeed } = useSelector((state: RootState) => state.appSettings.appSettings)

  const animationReady = result !== null && playState !== ANIMATION_PLAY_STATE.GAME_LOADING

  const dispatch = useDispatch()
  const { clear: clearAutoPlay, setTimeoutDelay: setAutoPlayDelay } = useTimeout(
    props.nextButtonControl.onClick,
    null,
  )

  const playPauseHandler = useCallback(() => {
    dispatch(AnimationActions.setPaused(!isPaused))
    Analytics.track(isPaused ? AnalyticEvents.PLAY_DRAWING : AnalyticEvents.PAUSE_DRAWING)
  }, [isPaused, dispatch])

  const restartDrawHandler = useCallback(() => {
    if (!result) return

    GameController.restart()
    Analytics.trackDrawing(AnalyticEvents.RESTART_DRAWING, result.drawNumber)
    clearAutoPlay()
  }, [result, clearAutoPlay])

  const skipToEndHandler = useCallback(() => {
    GameController.skipToEnd()
    Analytics.trackDrawing(AnalyticEvents.SKIP_TO_END_OF_DRAWING, result?.drawNumber ?? 0)
  }, [result])

  useEffect(() => {
    if (playState === ANIMATION_PLAY_STATE.FINISHED) {
      if (autoPlay) {
        setAutoPlayDelay(5000)
      }
    } else {
      clearAutoPlay()
    }
  }, [autoPlay, playState, dispatch, setAutoPlayDelay, clearAutoPlay])

  useEffect(() => {
    GameController.setPause(isPaused)
  }, [isPaused])

  useEffect(() => {
    return () => {
      clearAutoPlay()
    }
  }, [clearAutoPlay])

  useEffect(() => {
    if (playState === ANIMATION_PLAY_STATE.CRASHED) {
      throw new Error(`Crash Error: ${animationMessage}`)
    }
  }, [playState, animationMessage])

  useEffect(() => {
    GameController.updateGameSpeed(gameSpeed)
  }, [gameSpeed])

  useEffect(() => {
    if (!result) {
      GameController.stopDrawAnimation()
      return
    }

    document.documentElement.style.setProperty(
      '--app-background-color',
      `${ANIMATION_CONFIG_MAP[animationIdentifier].appSettings.backgroundBaseColor}`,
    )
    document.documentElement.style.setProperty(
      '--app-background-glow-color',
      `${ANIMATION_CONFIG_MAP[animationIdentifier].appSettings.backgroundGlowColor}`,
    )

    GameController.playDrawResult(result, animationIdentifier, {
      track: true,
      mode: result.ticketInfo.baseSelectedNumbers.length > 0 ? 'ticket' : 'view',
      drawResult: result,
    })
  }, [result, dispatch, animationIdentifier])

  return (
    <div className={`${CLASS_NAME}`}>
      <div className={`${CLASS_NAME}__draw-button-group`}>
        <button
          className={`${CLASS_NAME}__previous-draw-button ${GENERAL_BUTTON_CLASS}`}
          aria-label="Previous Draw"
          onClick={props.prevButtonControl.onClick}
          disabled={props.prevButtonControl.disabled}>
          <ReactSVG
            className={`${GENERAL_BUTTON_CLASS}__icon`}
            src="/icons/controls/previous-draw.svg"
          />
        </button>
        {props.jumpToDrawButtonControl ? (
          <button
            className={`${CLASS_NAME}__jump-to-draw-button ${GENERAL_BUTTON_CLASS}`}
            aria-label={hostConfig.config?.content.jumpToDraw.buttonLabel ?? 'Jump to Draw'}
            onClick={props.jumpToDrawButtonControl.onClick}
            disabled={props.jumpToDrawButtonControl.disabled}>
            <ReactSVG
              className={`${GENERAL_BUTTON_CLASS}__icon`}
              src="/icons/controls/jump-to-draw.svg"
            />
          </button>
        ) : null}
        <button
          className={`${CLASS_NAME}__next-draw-button ${GENERAL_BUTTON_CLASS}`}
          aria-label="Next Draw"
          onClick={props.nextButtonControl.onClick}
          disabled={props.nextButtonControl.disabled}>
          <ReactSVG
            className={`${GENERAL_BUTTON_CLASS}__icon`}
            src="/icons/controls/next-draw.svg"
          />
        </button>
      </div>
      <div className={`${CLASS_NAME}__animation-button-group`}>
        <button
          className={`${CLASS_NAME}__small-button ${GENERAL_BUTTON_CLASS}`}
          aria-label="Restart"
          onClick={restartDrawHandler}
          disabled={!animationReady}>
          <ReactSVG className={`${GENERAL_BUTTON_CLASS}__icon`} src="/icons/controls/restart.svg" />
        </button>
        <button
          className={`${CLASS_NAME}__big-button ${GENERAL_BUTTON_CLASS}`}
          aria-label="Play/Pause"
          onClick={playPauseHandler}
          disabled={!animationReady}>
          <ReactSVG
            className={`${GENERAL_BUTTON_CLASS}__icon${isPaused ? '__play' : ''}`}
            src={isPaused ? '/icons/controls/play.svg' : '/icons/controls/pause.svg'}
          />
        </button>

        <button
          className={`${CLASS_NAME}__small-button ${GENERAL_BUTTON_CLASS}`}
          aria-label="Skip To End"
          onClick={skipToEndHandler}
          disabled={!animationReady}>
          <ReactSVG
            className={`${GENERAL_BUTTON_CLASS}__icon`}
            src="/icons/controls/skip-to-end.svg"
          />
        </button>
      </div>
    </div>
  )
}
